/**
 * Calculate reading time for text content
 *
 * Handles code blocks differently than prose for more accurate estimates:
 * - Prose: ~200 words per minute (standard reading speed)
 * - Code blocks: ~80 words per minute (slower due to technical content)
 *
 * For technical CTO content with code samples, this provides significantly
 * more accurate reading time estimates than treating all content equally.
 */
export function calculateReadingTime(text: string): number {
  const PROSE_WORDS_PER_MINUTE = 200;
  const CODE_WORDS_PER_MINUTE = 80;

  if (!text.trim()) {
    return 1; // Minimum 1 minute for empty content
  }

  // Match fenced code blocks: ```lang\n...\n```
  const codeBlockRegex = /```[\s\S]*?```/g;
  const codeBlocks = text.match(codeBlockRegex) ?? [];

  let codeWords = 0;
  for (const block of codeBlocks) {
    // Remove opening/closing fences and optional language identifier
    const inner = block
      .replace(/```[\w+-]*/g, '')
      .replace(/```/g, '')
      .trim();

    if (inner) {
      codeWords += inner.split(/\s+/).filter(Boolean).length;
    }
  }

  // Remove code blocks from content to get prose-only text
  const proseContent = text.replace(codeBlockRegex, ' ');
  const proseWords = proseContent
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  const proseMinutes = proseWords / PROSE_WORDS_PER_MINUTE;
  const codeMinutes = codeWords / CODE_WORDS_PER_MINUTE;

  return Math.max(1, Math.ceil(proseMinutes + codeMinutes));
}

export function formatReadingTime(minutes: number): string {
  if (minutes === 1) {
    return '1 min read';
  }
  return `${minutes} min read`;
}

export function getWordCount(text: string): number {
  return text.trim().split(/\s+/).length;
}
